import React, { Fragment, memo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import type { OnboardingTemplate } from 'src/interfaces/onboardingTemplate'

interface Props {
  template: OnboardingTemplate | null
  isDeleting: boolean
  onConfirm(): void
  onClose(): void
}

const DeleteOnboardingTemplateModalComponent = ({
  template,
  isDeleting,
  onConfirm,
  onClose,
}: Props) => {
  if (!template) {
    return null
  }

  return (
    <CommonModal
      isOpen
      title="Delete this onboarding template?"
      onClose={onClose}
      buttons={
        <Fragment>
          {/* Disabled while the request is in flight, matching the page's dismissal guard. */}
          <Button size="small" color="grey" disabled={isDeleting} onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="small"
            color="red"
            loading={isDeleting}
            disabled={isDeleting}
            onClick={onConfirm}
            data-testid="onboarding-template-delete-confirm-button"
          >
            Delete template
          </Button>
        </Fragment>
      }
    >
      <div css={tw`flex flex-col gap-2`}>
        {/* The backend hard-deletes the template with all its steps and step configs in one
            transaction; nothing restores it. */}
        <Typography variant="body2">
          <b>{template.name}</b> and all of its steps will be permanently deleted. This cannot be
          undone.
        </Typography>
        <Typography variant="body2" css={tw`text-shades-500`}>
          A template that is still assigned to any contractor cannot be deleted — move those
          contractors to another template first.
        </Typography>
      </div>
    </CommonModal>
  )
}

export const DeleteOnboardingTemplateModal = memo(DeleteOnboardingTemplateModalComponent)
